import { useState, useEffect } from 'react';

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_KEY = 'agro_cookie_consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...prefs, date: new Date().toISOString() }));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const all: CookiePreferences = { necessary: true, analytics: true, marketing: true };
    setPreferences(all);
    saveConsent(all);
  };

  const handleRejectAll = () => {
    const minimal: CookiePreferences = { necessary: true, analytics: false, marketing: false };
    setPreferences(minimal);
    saveConsent(minimal);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePref = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      <div
        className="pointer-events-auto w-full max-w-2xl mx-4 mb-6 bg-white rounded-xl border border-gray-200 overflow-hidden"
        style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.13)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center text-[#2D5016]">
              <i className="ri-shield-check-line text-xl"></i>
            </div>
            <span className="font-semibold text-gray-800 text-base">Informativa sui Cookie</span>
          </div>
          <button
            onClick={handleRejectAll}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Chiudi"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Utilizziamo i cookie per migliorare la tua esperienza di navigazione, analizzare il traffico del sito e personalizzare i contenuti.
            Puoi scegliere quali cookie accettare. I cookie strettamente necessari non possono essere disattivati.{' '}
            <button
              type="button"
              onClick={() => setShowDetails(v => !v)}
              className="text-[#2D5016] hover:underline cursor-pointer font-medium"
            >
              {showDetails ? 'Nascondi dettagli' : 'Gestisci preferenze'}
            </button>
          </p>

          {/* Dettagli categorie */}
          {showDetails && (
            <div className="mt-4 space-y-3">
              {/* Necessari */}
              <div className="flex items-start justify-between gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">Cookie Necessari</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Essenziali per il funzionamento del sito. Non possono essere disattivati.
                  </p>
                </div>
                <div className="flex items-center mt-0.5">
                  <div className="w-10 h-5 bg-[#2D5016] rounded-full flex items-center justify-end px-0.5 opacity-60 cursor-not-allowed">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Analitici */}
              <div className="flex items-start justify-between gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">Cookie Analitici</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Ci aiutano a capire come gli utenti interagiscono con il sito (es. Google Analytics).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => togglePref('analytics')}
                  className={`mt-0.5 w-10 h-5 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${
                    preferences.analytics ? 'bg-[#2D5016] justify-end' : 'bg-gray-300 justify-start'
                  }`}
                  aria-label="Toggle cookie analitici"
                >
                  <div className="w-4 h-4 bg-white rounded-full transition-all"></div>
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">Cookie di Marketing</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Utilizzati per mostrare annunci pertinenti e misurare l&apos;efficacia delle campagne pubblicitarie.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => togglePref('marketing')}
                  className={`mt-0.5 w-10 h-5 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${
                    preferences.marketing ? 'bg-[#2D5016] justify-end' : 'bg-gray-300 justify-start'
                  }`}
                  aria-label="Toggle cookie marketing"
                >
                  <div className="w-4 h-4 bg-white rounded-full transition-all"></div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer azioni */}
        <div className="px-6 pb-5 flex flex-col sm:flex-row gap-2 justify-end">
          <button
            onClick={handleRejectAll}
            className="px-5 py-2 text-sm text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Rifiuta tutti
          </button>
          {showDetails && (
            <button
              onClick={handleSavePreferences}
              className="px-5 py-2 text-sm text-[#2D5016] border border-[#2D5016] rounded-full hover:bg-[#2D5016]/5 transition-colors cursor-pointer whitespace-nowrap"
            >
              Salva preferenze
            </button>
          )}
          <button
            onClick={handleAcceptAll}
            className="px-5 py-2 text-sm bg-[#2D5016] text-white rounded-full hover:bg-[#3d6b1e] transition-colors cursor-pointer whitespace-nowrap"
          >
            Accetta tutti
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
